import React, { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import Sidebar from './Sidebar';
import { MENU_ANCHO } from '@/utils/navegacion';
import Topbar from './Topbar';
import { useUIStore } from '@/store/ui.store';
import { useSocket } from '@context/SocketContext';
import { useAuth } from '@context/AuthContext';
import { CasaTemaContext, PASO, useTema, useTemaDelMarco } from '@components/M02/casaTema';

const SIDEBAR_EXPANDED  = MENU_ANCHO.abierto;
const SIDEBAR_COLLAPSED = MENU_ANCHO.colapsado;

// ─── Icono de módulo para el toast ───────────────────────────
const MODULO_EMOJI: Record<string, string> = {
  ingresos:      '💰',
  egresos:       '💸',
  bancos:        '🏦',
  proyectos:     '🏗️',
  creditos:      '📊',
  ventas:        '🤝',
  obra:          '🔨',
  tareas:        '✅',
  postventa:     '🔧',
  reportes:      '📄',
  configuracion: '⚙️',
};

// ─── Listener de notificaciones WS ───────────────────────────
const NotifListener: React.FC = () => {
  const { onNotificacion, onBadgeCount } = useSocket();
  const setNoLeidas       = useUIStore((s) => s.setNoLeidas);
  const incrementNoLeidas = useUIStore((s) => s.incrementNoLeidas);
  const qc = useQueryClient();
  const T = useTema();

  // Sincronizar badge count desde WS (connected + badge_count)
  useEffect(() => {
    const off = onBadgeCount(({ no_leidas }) => {
      setNoLeidas(no_leidas);
    });
    return off;
  }, [onBadgeCount, setNoLeidas]);

  // Mostrar toast al recibir nueva notificación
  useEffect(() => {
    const off = onNotificacion((notif) => {
      const emoji = MODULO_EMOJI[notif.modulo] ?? '🔔';
      toast(
        `${emoji} ${notif.mensaje}`,
        {
          duration: 5000,
          style: {
            background: T.fg,
            color: T.panelSolid,
            fontSize: PASO.s13,
            maxWidth: '360px',
            padding: '10px 14px',
          },
          iconTheme: { primary: T.estados.cfo, secondary: T.panelSolid },
        },
      );

      // Push del navegador (Fase 6a) — solo si el usuario dio permiso y la
      // pestaña NO está enfocada (evita duplicar el toast que ya se ve in-app).
      // v1 sin service worker: solo funciona con la app abierta.
      if (
        typeof Notification !== 'undefined' &&
        Notification.permission === 'granted' &&
        document.hidden
      ) {
        try {
          new Notification(`${emoji} ERP RM Desarrollos`, {
            body: notif.mensaje,
            tag: `notif-${notif.id}`,   // colapsa duplicados de la misma notif
          });
        } catch {
          // Safari puede lanzar si se llama fuera de un gesto; se ignora en silencio
        }
      }

      // Incrementar badge localmente
      incrementNoLeidas();
      // Invalidar lista de notificaciones (si la página está abierta)
      qc.invalidateQueries({ queryKey: ['notificaciones'] });
    });
    return off;
  }, [onNotificacion, setNoLeidas, incrementNoLeidas, qc, T]);

  return null;
};

// ─── Onboarding: abre asistente en primer login ───────────────
const OnboardingTrigger: React.FC = () => {
  const { usuario } = useAuth();
  const setAIAssistantOpen = useUIStore((s) => s.setAIAssistantOpen);

  useEffect(() => {
    if (!usuario?.id) return undefined;
    const key = `erp_onboarding_done_${usuario.id}`;
    if (localStorage.getItem(key)) return undefined;
    localStorage.setItem(key, '1');
    const t = setTimeout(() => setAIAssistantOpen(true), 800);
    return () => clearTimeout(t);
  }, [usuario?.id, setAIAssistantOpen]);

  return null;
};

const AppLayout: React.FC = () => {
  const { sidebarAbierto } = useUIStore();
  const w = sidebarAbierto ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED;
  const T = useTema();
  // I-242: el marco (menú, barra, paleta) se viste con el tema vigente; el contenido de las pantallas NO cuelga de este
  // proveedor (cada pantalla sigue con su tema: el claro de respaldo, o el de la casa dentro de ella).
  const TMarco = useTemaDelMarco();

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: T.bg }}>
      <CasaTemaContext.Provider value={TMarco}><Sidebar /></CasaTemaContext.Provider>

      <div
        className="flex flex-col flex-1 overflow-hidden"
        style={{
          marginLeft: w + 20,
          transition: 'margin-left 220ms cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <CasaTemaContext.Provider value={TMarco}><Topbar /></CasaTemaContext.Provider>

        <main
          className="flex-1 overflow-y-auto flex flex-col"
          style={{ paddingTop: 84 }}
        >
          <div className="p-6 flex-1 flex flex-col">
            <Outlet />
          </div>
        </main>
      </div>

      <NotifListener />
      <OnboardingTrigger />
    </div>
  );
};

export default AppLayout;
